// src/utils/productRecommendations.js
//
// Relevance-first product recommendation ranking for Orrith.
//
//  - Pure, dependency-free ranking logic so it is fully unit-testable without
//    Firebase. All Firestore access lives behind the repository layer.
//  - Relevance is the primary signal: category -> hierarchy -> title/description
//    overlap -> state/storage. No unrelated listing outranks a related match
//    merely because it shares an admin zone.
//  - Location is a *boost*, not a gate. Rwanda is broadened progressively
//    (cell -> sector -> district -> province -> rest) via a decaying proximity
//    score, so sparse local supply never forces irrelevant fill.
//  - Personalization only uses the viewer's own saved wishlist; no other user
//    activity is read or exposed.
//  - Eligibility (visibility / sold / expired / deleted / own-listings) is
//    centralized here so the business rules live in one place.
//  - Weights are exported as a plain object so they can be tuned without code
//    changes; treat the defaults as starting points, not guarantees.

import { isProductVisible, isProductSold } from '../constants/farmConstants';
import { isItemExpired } from './formatExpiration';
import {
  getCategoryHierarchyMeta,
  resolveCategoryId,
} from './agriculturalCategories';
import { distanceInKm } from './distance';

// ── Configurable ranking weights (starting points) ───────────────────────
export const DEFAULT_RANKING_WEIGHTS = Object.freeze({
  categoryExact: 100,
  categoryGroup: 40,
  categoryMacro: 20,
  titleTokenOverlap: 60,
  descriptionTokenOverlap: 20,
  productState: 15,
  storageCondition: 10,
  sameSeller: 55,
  locationCell: 45,
  locationSector: 30,
  locationDistrict: 20,
  locationProvince: 10,
  geoDistance: 40,
  priceCompatibility: 25,
  freshness: 20,
  wishlistBoost: 30,
  recentSellerActivity: 5,
});

// Tunables for candidate volume and the progressive geographic fallback.
export const RECOMMENDATIONS_LIMIT = 8;
export const RECOMMENDATIONS_CANDIDATE_LIMIT = 120;
export const GEO_DISTANCE_MAX_KM = 150; // score falls to 0 beyond this range

// ── Identity / location normalization ────────────────────────────────────

export function getSellerId(product) {
  if (!product) return null;
  return (
    product.userId ||
    product.poster?.userId ||
    product.sellerId ||
    product.poster?.uid ||
    null
  );
}

export function getProductId(product) {
  if (!product) return null;
  return product.id || product.productId || null;
}

export function normalizeCategoryId(product) {
  const raw = product?.categoryId || product?.category || '';
  return resolveCategoryId(raw) || String(raw || '').toLowerCase();
}

/**
 * Normalize the Rwandan location hierarchy for comparison.
 * Accepts the structured {province,district,sector,cell} object and/or
 * legacy aliases, plus a lat/lng pair when present.
 */
export function normalizeLocation(loc) {
  if (!loc || typeof loc !== 'object') {
    return { cell: null, sector: null, district: null, province: null, lat: null, lon: null };
  }
  const norm = (v) => {
    if (v == null || v === '') return null;
    return String(v).toLowerCase().trim();
  };
  const toCoord = (v) => {
    if (v == null || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const latVal =
    loc.latitude ?? loc.lat ?? (Array.isArray(loc.latlng) ? loc.latlng[0] : null);
  const lonVal =
    loc.longitude ?? loc.lng ?? loc.lon ?? (Array.isArray(loc.latlng) ? loc.latlng[1] : null);
  return {
    cell: norm(loc.cell || loc.village || loc.cellule),
    sector: norm(loc.sector || loc.scecture),
    district: norm(loc.district || loc.city || loc.distrit),
    province: norm(loc.province || loc.region || loc.imirenge),
    lat: toCoord(latVal),
    lon: toCoord(lonVal),
  };
}

/**
 * Best admin-level where `a` is contained within `b`.
 * Returns 'cell' | 'sector' | 'district' | 'province' | null.
 */
export function locationTierMatch(a, b) {
  if (!a || !b) return null;
  if (a.cell && b.cell && a.cell === b.cell) return 'cell';
  if (a.sector && b.sector && a.sector === b.sector) return 'sector';
  if (a.district && b.district && a.district === b.district) return 'district';
  if (a.province && b.province && a.province === b.province) return 'province';
  return null;
}

/**
 * Geographic boost (0..1) based on admin containment *and* haversine distance
 * when both listings carry coordinates. Cell-level match dominates; otherwise
 * proximity decays with distance so nearby-but-different-cell still wins over
 * unrelated distant matches.
 */
export function locationBoost(candidateLoc, targetLoc, weights) {
  const tier = locationTierMatch(candidateLoc, targetLoc);
  let base = 0;
  if (tier === 'cell') base = weights.locationCell;
  else if (tier === 'sector') base = weights.locationSector;
  else if (tier === 'district') base = weights.locationDistrict;
  else if (tier === 'province') base = weights.locationProvince;
  else base = 0;

  if (
    candidateLoc.lat != null &&
    candidateLoc.lon != null &&
    targetLoc.lat != null &&
    targetLoc.lon != null
  ) {
    const km = distanceInKm(
      { lat: candidateLoc.lat, lng: candidateLoc.lon },
      { lat: targetLoc.lat, lng: targetLoc.lon }
    );
    const geoScore = Math.max(0, 1 - km / GEO_DISTANCE_MAX_KM) * weights.geoDistance;
    // Proximity only adds to an existing admin match (or is small otherwise) so
    // that geographic closeness never overrides product relevance.
    return base + (tier ? geoScore * 0.5 : geoScore * 0.2);
  }
  return base;
}

// ── Text similarity helpers ──────────────────────────────────────────────

export function tokenize(text) {
  if (!text) return [];
  return String(text)
    .toLowerCase()
    .split(/[^a-z0-9]+/i)
    .filter(Boolean);
}

export function jaccard(tokensA, tokensB) {
  if (!tokensA.length && !tokensB.length) return 0;
  const setA = new Set(tokensA);
  const setB = new Set(tokensB);
  let intersection = 0;
  for (const token of setA) if (setB.has(token)) intersection += 1;
  const union = setA.size + setB.size - intersection;
  return union === 0 ? 0 : intersection / union;
}

function toMillis(ts) {
  if (!ts) return 0;
  if (typeof ts?.toMillis === 'function') return ts.toMillis();
  if (typeof ts?.toDate === 'function') return ts.toDate().getTime();
  if (typeof ts === 'number') return ts;
  const n = Date.parse(ts);
  return Number.isFinite(n) ? n : 0;
}

// Price helpers
function numericPrice(product) {
  const n = Number(product?.pricePerKg ?? product?.price ?? 0);
  return Number.isFinite(n) ? n : null;
}

/**
 * Price compatibility: how close the candidate price is to the target's,
 * normalized to 0..1 (closer is better). Unpriced or zero-price listings stay
 * neutral rather than penalized.
 */
function priceCompatibilityScore(candidate, target, weight) {
  const a = numericPrice(candidate);
  const b = numericPrice(target);
  if (a == null || b == null || b === 0) return 0;
  if (a === 0) return 0;
  const ratio = Math.min(a, b) / Math.max(a, b);
  // ratio in [0,1]; 1 => identical price, 0 => wildly different
  return weight * ratio;
}

/**
 * Recency factor 0..1 over a 14-day window. Newer listings score higher.
 */
function freshnessScore(product, weight) {
  const ms = toMillis(product?.createdAt) || toMillis(product?.updatedAt);
  if (!ms) return 0;
  const ageDays = (Date.now() - ms) / (1000 * 60 * 60 * 24);
  if (ageDays <= 0) return weight;
  return weight * Math.max(0, 1 - ageDays / 14);
}

// ── Eligibility ─────────────────────────────────────────────────────────

/**
 * A product is eligible to be recommended when it is publicly visible, active,
 * not expired, not sold, and not one of the viewer's own listings, and not the
 * product currently being viewed.
 *
 * @param {object} candidate
 * @param {object} opts { currentProductId, buyerId }
 * @returns {boolean}
 */
export function isEligibleRecommendation(candidate, { currentProductId, buyerId }) {
  if (!candidate) return false;
  const id = getProductId(candidate);
  if (!id) return false;
  if (id === currentProductId) return false;

  const candidateSellerId = getSellerId(candidate);
  if (buyerId && candidateSellerId && String(candidateSellerId) === String(buyerId)) {
    return false;
  }

  if (!isProductVisible(candidate)) return false;
  if (isItemExpired(candidate)) return false;
  if (isProductSold(candidate)) return false;
  return true;
}

// ── Core ranking ─────────────────────────────────────────────────────────

/**
 * Score a single candidate against the target product. Higher is better.
 *
 * Score composition (all weighted):
 *   relevance = categoryExact | categoryGroup | categoryMacro    (hierarchy)
 *             + titleTokenOverlap | descriptionTokenOverlap
 *             + productState | storageCondition
 *   geo       = location boost (admin tier + optional haversine)
 *   seller    = sameSeller, recentSellerActivity
 *   price     = priceCompatibility
 *   freshness = recency
 *   personal  = wishlistBoost
 *
 * Relevance always outranks an unrelated listing; geography only *boosts*
 * matches that are already relevant.
 *
 * @param {object} candidate
 * @param {object} target   the product currently being viewed
 * @param {object} ctx      { wishlistProductIds, userCoords }
 * @param {object} weights  DEFAULT_RANKING_WEIGHTS override
 * @returns {number}
 */
export function scoreProduct(candidate, target, ctx = {}, weights = DEFAULT_RANKING_WEIGHTS) {
  const w = { ...DEFAULT_RANKING_WEIGHTS, ...weights };

  // --- Category hierarchy (relevance) ---
  const targetCat = normalizeCategoryId(target);
  const candidateCat = normalizeCategoryId(candidate);
  let categoryScore = 0;
  if (targetCat && candidateCat === targetCat) {
    categoryScore = w.categoryExact;
  } else {
    const targetMeta = getCategoryHierarchyMeta(targetCat);
    const candidateMeta = getCategoryHierarchyMeta(candidateCat);
    if (targetMeta && candidateMeta) {
      if (targetMeta.groupId && candidateMeta.groupId === targetMeta.groupId) {
        categoryScore = w.categoryGroup;
      } else if (
        targetMeta.macroCategoryId &&
        candidateMeta.macroCategoryId === targetMeta.macroCategoryId
      ) {
        categoryScore = w.categoryMacro;
      }
    }
  }

  // --- Title / description overlap ---
  const titleJaccard = jaccard(tokenize(target.title), tokenize(candidate.title));
  const titleScore = titleJaccard * w.titleTokenOverlap;

  const candidateDesc =
    candidate.description || candidate.desc || candidate.title || '';
  const targetDesc = target.description || target.desc || target.title || '';
  const descJaccard = jaccard(tokenize(targetDesc), tokenize(candidateDesc));
  const descScore = descJaccard * w.descriptionTokenOverlap;

  // --- Product state / storage ---
  const productStateScore =
    target.productState && candidate.productState === target.productState
      ? w.productState
      : 0;
  const storageScore =
    target.storageCondition &&
    candidate.storageCondition === target.storageCondition
      ? w.storageCondition
      : 0;

  let relevance = categoryScore + titleScore + descScore + productStateScore + storageScore;

  // --- Geography (boost only; never overrides empty relevance) ---
  const targetLoc = normalizeLocation(target.location);
  const candidateLoc = normalizeLocation(candidate.location);
  const geoScore = locationBoost(candidateLoc, targetLoc, w);
  if (ctx.userCoords && candidateLoc.lat != null && candidateLoc.lon != null) {
    const km = distanceInKm(
      { lat: candidateLoc.lat, lng: candidateLoc.lon },
      ctx.userCoords
    );
    const userGeoBoost = Math.max(0, 1 - km / GEO_DISTANCE_MAX_KM) * w.geoDistance * 0.2;
    geoScore = geoScore + userGeoBoost;
  }

  // --- Seller relationship ---
  let sellerScore = 0;
  if (getSellerId(candidate) && getSellerId(candidate) === getSellerId(target)) {
    sellerScore += w.sameSeller;
    sellerScore += w.recentSellerActivity;
  }

  // --- Price compatibility ---
  const priceScore = priceCompatibilityScore(candidate, target, w.priceCompatibility);

  // --- Freshness ---
  const freshScore = freshnessScore(candidate, w.freshness);

  // --- Personalization (viewer's own wishlist only) ---
  const wishIds = ctx.wishlistProductIds || [];
  const wishScore = wishIds.includes(getProductId(candidate))
    ? w.wishlistBoost
    : 0;

  const total = relevance + geoScore + sellerScore + priceScore + freshScore + wishScore;

  return {
    total,
    breakdown: {
      categoryScore,
      relevance,
      geoScore,
      sellerScore,
      priceScore,
      freshness: freshScore,
      wishlist: wishScore,
    },
  };
}

/**
 * Rank eligible candidates for `target`.
 *
 * Progressive geographic broadening is achieved structurally: every eligible
 * candidate is scored (location only boosts relevance), so sparse local supply
 * naturally yields farther-but-still-relevant results rather than padding the
 * list with unrelated listings just to fill slots.
 *
 * @param {object} opts
 * @param {object} opts.target         product being viewed
 * @param {object[]} opts.candidates   eligible, deduped product rows
 * @param {string} [opts.buyerId]      viewer uid (excludes own listings)
 * @param {string[]} [opts.wishlistProductIds] viewer's saved product ids
 * @param {{lat,lng}} [opts.userCoords] viewer coordinates for proximity
 * @param {number} [opts.limit]        max recommendations to return
 * @param {object} [opts.weights]      weight overrides
 * @returns {{ recommendations: object[], fromCache: boolean }}
 */
export function rankProductRecommendations({
  target,
  candidates,
  buyerId,
  wishlistProductIds = [],
  userCoords = null,
  limit = RECOMMENDATIONS_LIMIT,
  weights,
} = {}) {
  if (!target || !Array.isArray(candidates) || candidates.length === 0) {
    return { recommendations: [], fromCache: false };
  }

  const currentProductId = getProductId(target);
  const scored = [];
  const seen = new Set();

  for (const candidate of candidates) {
    const id = getProductId(candidate);
    if (!id || seen.has(id)) continue; // dedupe
    seen.add(id);
    if (!isEligibleRecommendation(candidate, { currentProductId, buyerId })) continue;
    const { total, breakdown } = scoreProduct(candidate, target, {
      wishlistProductIds,
      userCoords,
    }, weights);
    scored.push({ product: candidate, score: total, breakdown });
  }

  // Stable, deterministic ordering: score desc, then recency desc, then id.
  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return (
      toMillis(b.product.createdAt) - toMillis(a.product.createdAt) ||
      String(a.product.id || '').localeCompare(String(b.product.id || ''))
    );
  });

  const recommendations = scored.slice(0, limit).map((entry) => entry.product);
  return { recommendations, scored, fromCache: false };
}

export default rankProductRecommendations;
